'use client';

import React from 'react';
import dynamic from 'next/dynamic';

import { RotateCcwIcon } from 'lucide-react';
import { useLocalStorage } from 'react-use';

import { Link } from '~/components/common/link';
import { Button } from '~/components/ui/button';
import { cn } from '~/lib/utils';
import { shuffleArray } from '~/utils/array';
import { clamp } from '~/utils/number';

import { GameHighScore } from '../../_components/game-high-score';
import GameResultDialog from '../../_components/game-result-dialog';
import { GameScore } from '../../_components/game-score';
import { OrganGameBoardSkeleton } from './organ-game-board';

const OrganGameBoard = dynamic(() => import('./organ-game-board').then((mod) => mod.OrganGameBoard), {
  loading: () => <OrganGameBoardSkeleton />,
  ssr: false,
});

const LEVELS = [
  {
    id: 'lungs',
    name: 'Lungs',
    content:
      'The lungs function in your respiratory system to extract oxygen from the air and transfer it into the bloodstream. Your lungs release carbon dioxide from the bloodstream into the atmosphere in a process of gas exchange.',
  },
  {
    id: 'heart',
    name: 'Heart',
    content:
      'The heart is a muscular organ in most animals, which pumps blood through the blood vessels of the circulatory system. The pumped blood carries oxygen and nutrients to the body, while carrying metabolic waste such as carbon dioxide to the lungs.',
  },
  {
    id: 'kidneys',
    name: 'Kidneys',
    content:
      'The kidney participates in the control of the volume of various body fluids, fluid osmolality. acid-base balance, various electrolyte concentrations, and removal of toxins. Filtration occurs in the glomerulus: one fifth of the blood volume that enters the kidneys is filtered.',
  },
  {
    id: 'liver',
    name: 'Liver',
    content:
      'The liver is an accessory digestive organ that produces bile, an alkaline fluid, which helps the breakdown of fat. The gallbladder, a small pouch that sits just under the liver, stores bile produced by the liver which is afterwards moved to the small intestine to complete digestion.',
  },
  {
    id: 'stomach',
    name: 'Stomach',
    content:
      'In the digestive system the stomach is involved in the second phase of digestion, following chewing. It performs a chemical breakdown by means of enzymes and hydrochloric acid.',
  },
  {
    id: 'small_intestine',
    name: 'Small Intestine',
    content:
      'The small intestine or small bowel is an organ in the gastrointestinal tract where most of the absorption of nutrients from food takes place. It lies between the stomach and large intestine, and receives bile and pancreatic juice through the pancreatic duct to aid in digestion.',
  },
  {
    id: 'large_intestine',
    name: 'Large Intestine',
    content:
      'The large intestine, also known as the large bowel, is the last part of the gastrointestinal tract and of the digestive system in vertebrates. Water is absorbed here and the remaining waste material is stored in the rectum as feces before being removed by defecation.',
  },
];

function createShuffledLevels() {
  return shuffleArray(LEVELS);
}

function getOrganGameResultTitle({
  finished,
  newHighScore,
  totalScore,
  levelName,
}: {
  finished: boolean;
  newHighScore: boolean;
  totalScore: number;
  levelName: string;
}) {
  if (!finished) {
    return `Correct! That's your ${levelName}!`;
  }
  if (totalScore === 100) {
    return 'A Perfect Score!';
  }
  if (newHighScore) {
    return 'New Best Score!';
  }
  return 'Great Job!';
}

const movesScoreWeight = LEVELS.length;

export default function OrganGame() {
  const [levels, setLevels] = React.useState(createShuffledLevels);
  const [gameSessionId, setGameSessionId] = React.useState(0);
  const [moves, setMoves] = React.useState<number>(0);
  const [level, setLevel] = React.useState<number>(0);
  const [finished, setFinished] = React.useState<boolean>(false);
  const [newHighScore, setNewHighScore] = React.useState<boolean>(false);
  const [resultsModalOpen, setResultsModalOpen] = React.useState<boolean>(false);

  const currentLevel = React.useMemo(() => levels[level], [level, levels]);

  const [bestScore, setBestScore] = useLocalStorage<number>('organ-game-best-score');
  const [bestMoves, setBestMoves] = useLocalStorage<number>('organ-game-best-moves');

  const movesScore = React.useMemo(() => moves - (level + 1), [level, moves]);
  const totalScore = React.useMemo(
    () => clamp(0, Math.floor((100 - movesScore * movesScoreWeight) * ((level + 1) / levels.length)), 100),
    [movesScore, level, levels.length],
  );

  const answers = React.useMemo(() => {
    const others = shuffleArray(levels.filter((level) => level.id !== currentLevel.id));
    return shuffleArray([currentLevel, ...others.slice(0, 2)]);
  }, [currentLevel, levels]);

  const headerContent = React.useMemo(
    () => (
      <svg
        className={cn('h-full w-full', !finished && 'max-h-32')}
        fill='none'
        focusable='false'
        role='img'
        xmlns='http://www.w3.org/2000/svg'
      >
        <use xlinkHref={`#${finished ? 'all-organs' : currentLevel.id}`} />
      </svg>
    ),
    [finished, currentLevel.id],
  );

  const nextLevel = React.useCallback(() => {
    setResultsModalOpen(false);
    setLevel(level + 1);
  }, [level]);

  const restartGame = React.useCallback(() => {
    setLevels(createShuffledLevels());
    setGameSessionId((id) => id + 1);
    setMoves(0);
    setLevel(0);
    setFinished(false);
    setNewHighScore(false);
    setResultsModalOpen(false);
  }, []);

  const finishGame = React.useCallback(() => {
    if (level + 1 === levels.length) {
      const bests = {
        score: bestScore ?? 0,
        moves: bestMoves ?? Number.MAX_SAFE_INTEGER,
      };

      // Update best score, tie-break
      if (totalScore > bests.score || (totalScore === bests.score && moves < bests.moves)) {
        setBestMoves(moves);
        setBestScore(totalScore);
        setNewHighScore(true);
      }
    }

    setTimeout(() => setResultsModalOpen(true), 400);
  }, [bestMoves, bestScore, level, levels.length, moves, setBestMoves, setBestScore, totalScore]);

  return (
    <div className='flex flex-col items-center'>
      <OrganGameBoard
        answer={currentLevel}
        answers={answers}
        finishGame={finishGame}
        key={`${gameSessionId}-${currentLevel.id}`}
        setMoves={setMoves}
      />

      <GameResultDialog
        open={resultsModalOpen}
        setOpen={setResultsModalOpen}
        headerContent={headerContent}
        title={getOrganGameResultTitle({
          finished,
          newHighScore,
          totalScore,
          levelName: currentLevel.name,
        })}
        description={currentLevel.content}
        showConfetti={finished && resultsModalOpen}
      >
        {finished ? (
          <>
            <GameScore moves={moves} movesLabel='Attempts' score={`${totalScore}%`} />
            {bestMoves != null && bestScore != null && (
              <GameHighScore moves={bestMoves} movesLabel='Attempts' score={`${bestScore}%`} />
            )}
          </>
        ) : (
          <>
            {level + 1 < levels.length && (
              <div className='mt-5 flex flex-col gap-y-3'>
                <Button onClick={nextLevel}>Next Question</Button>

                <Button variant='outline-secondary' onClick={restartGame}>
                  <RotateCcwIcon />
                  Restart Game
                </Button>
              </div>
            )}
            {level + 1 === levels.length && !finished && (
              <Button className='w-full' onClick={() => setFinished(true)}>
                Show My Score
              </Button>
            )}
          </>
        )}
        {finished && (
          <div className='mt-6 flex flex-wrap gap-3'>
            <Button className='flex-1' onClick={restartGame}>
              <RotateCcwIcon />
              Play Again
            </Button>

            <Button className='flex-1' variant='outline-secondary' asChild>
              <Link href='/games'>Back to Games</Link>
            </Button>
          </div>
        )}
      </GameResultDialog>
    </div>
  );
}
